import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Save } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Resource } from '../../../types';
import { BasicResourceConfigurationForm } from '../ResourceSelectorComponents/BasicResourceConfigurationForm';

interface ResourceConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: Record<string, any>) => void;
  resource: Resource | null;
  currentConfig?: Record<string, any>;
}

export const ResourceConfigModal: React.FC<ResourceConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  resource,
  currentConfig = {}
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<Record<string, any>>(currentConfig || {});

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const renderConfigFields = () => {
    if (!resource) return null;


    return (
        <BasicResourceConfigurationForm 
            resource={resource} 
            config={config}
            onUpdate={(params) => {
                setConfig({ ...config, ...params });
            }} 
            idEditable={true}
          />
    );
  };



  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Settings className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('timelineSteps.configureResource')}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {resource && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    {resource.type} ({t('resourceConfig.overrideSettings')})
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('resourceConfig.overrideDescription')}
                  </p>
                </div>
              )}

              {renderConfigFields()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{t('actions.save')}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
